import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment.development';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class CaseService {
    private apiUrl = `${environment.apiUrl}/api/case-submissions`;
    private casesUrl = `${environment.apiUrl}/api/cases`;
    private boardUrl = `${environment.apiUrl}/api/caseboard`;
    private offersUrl = `${environment.apiUrl}/api/case-offers`;

    constructor(private http: HttpClient, private authService: AuthService) { }

    submitCase(data: any): Observable<any> {
        return this.http.post<any>(this.apiUrl, data);
    }

    createOffer(data: any): Observable<any> {
        return this.http.post<any>(this.offersUrl, data);
    }

    getOfferByCaseId(caseId: string | number, silent = false): Observable<any> {
        const headers = silent ? new HttpHeaders({ 'X-Skip-Loader': 'true' }) : undefined;
        return this.http.get<any>(`${this.offersUrl}/${caseId}`, { headers });
    }

    acceptOffer(offerId: number): Observable<any> {
        return this.http.post<any>(`${this.offersUrl}/${offerId}/accept`, { offer_status: 'ACCEPTED' });
    }

    rejectOffer(offerId: number): Observable<any> {
        return this.http.post<any>(`${this.offersUrl}/${offerId}/reject`, { offer_status: 'REJECTED' });
    }

    getOfferDocumentTypes(caseId: number | string): Observable<any[]> {
        return this.http.get<any[]>(`${this.offersUrl}/${caseId}/document_type`, {
            headers: new HttpHeaders({ 'X-Skip-Loader': 'true' })
        });
    }

    getCaseBoard(): Observable<any> {
        const role = this.authService.getRole();
        let params = new HttpParams();
        if (role) {
            params = params.set('role', role);
        }
        return this.http.get<any>(this.boardUrl, { params });
    }

    getCaseById(id: number | string): Observable<any> {
        return this.http.get<any>(`${this.casesUrl}/${id}`);
    }

    getDocumentTypeList(caseId: number | string): Observable<any[]> {
        return this.http.get<any[]>(`${environment.apiUrl}/api/document_type_list/${caseId}`);
    }

    getCaseDocuments(caseId: number | string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/api/case_docs/${caseId}`, {
            headers: new HttpHeaders({ 'X-Skip-Loader': 'true' })
        });
    }

    uploadCaseDocument(caseId: number | string, files: File | File[]): Observable<any> {
        const formData = new FormData();
        formData.append('case_id', caseId.toString());
        const fileArray = Array.isArray(files) ? files : [files];
        for (const file of fileArray) {
            formData.append('documents[]', file);
        }
        return this.http.post<any>(`${environment.apiUrl}/api/additional_docs`, formData);
    }

    deleteCaseDocument(documentId: number | string): Observable<any> {
        return this.http.delete<any>(`${environment.apiUrl}/api/additional_docs/${documentId}`);
    }

    generateClientLink(caseId: number | string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/api/generateClientLink/${caseId}`);
    }

    submitHospitalPayment(data: FormData): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/api/assistant/hospital_payments`, data);
    }

    updateHospitalPaymentStatus(data: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/api/update_hospital_payment_status`, data);
    }

    submitPaymentDetails(data: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/api/payments`, data);
    }

    getPaymentDetails(caseId: string | number): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/api/payments`, {
            params: { case_id: caseId.toString() }
        });
    }

    updatePaymentStatus(data: FormData): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/api/payments`, data);
    }

    acceptDocument(documentId: number, status: 1 | 2): Observable<any> {
        const formData = new FormData();
        formData.append('document_id', documentId.toString());
        formData.append('status', status.toString());
        return this.http.post<any>(`${environment.apiUrl}/api/accept_document`, formData);
    }

    /**
     * Uploads all e-signed PDF documents to /api/upload-signed.
     * Payload (multipart/form-data):
     *   case_id
     *   documents[0][type]  = 2 | 3 | 4   (API document type)
     *   documents[0][file]  = <PDF blob>
     *   documents[1][type]  = ...
     *   documents[1][file]  = ...
     */
    uploadSignedDocuments(
        caseId: number | string,
        documents: { id: number | string; file: File }[],
        clientAuth?: { token: string; pin: string },
    ): Observable<any> {
        const formData = new FormData();
        formData.append('case_id', caseId.toString());
        documents.forEach((doc, index) => {
            formData.append(`documents[${index}][type]`, doc.id.toString());
            formData.append(`documents[${index}][file]`, doc.file);
        });
        if (clientAuth) {
            formData.append('token', clientAuth.token);
            formData.append('pin', clientAuth.pin);
            return this.http.post<any>(`${environment.apiUrl}/api/client/upload_signed`, formData);
        }
        return this.http.post<any>(`${environment.apiUrl}/api/upload-signed`, formData);
    }

    verifyClientPin(token: string, pin: string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/api/client/case`, {
            params: { token, pin },
        });
    }

    uploadReleaseOfLien(caseId: number | string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('case_id', caseId.toString());
        formData.append('documents', file);
        return this.http.post<any>(`${environment.apiUrl}/api/document_Release_of_lien`, formData);
    }

    receivedMoney(data: FormData): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/api/received_money`, data);
    }

    getAssistantCaseList(): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/api/assistant/case-list`);
    }
}
